import { fetchRoomIdForAgentId } from '#~/api/agent/index.js';
import { toIdFromHex } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/index.js';
import { depot } from '#~/depot/index.js';
import { NotFoundError, StudioError } from '#~/error/index.js';
import { broker } from '#~/livekit/broker.js';
import { Paths } from '#~/routes/index.js';
import { Loader } from '@studio/design-system/loader';
import type { Props } from './types.js';
import { unwrap } from '@studio/js-utils/async';
import { normalizeId } from '@studio/models';
import type { Agent as M_Agent } from '@studio/models/agent';
import { useAsyncEffect } from '@studio/react-utils/hooks';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const WAIT_POLL_INTERVAL_MS = 250;
const MAX_WAIT_FOR_PARTICIPANT_MS = 17_000;

/** `<AgentLookup>` route. */
export const AgentLookupAndWait = (_props: Props): ReactElement => {
  const params = useParams();

  const agentId = normalizeId<M_Agent['id']>(params.agentId);
  const { getAccessTokenSilently } = useAuth0();
  const [error, setError] = useState<boolean>();

  if (!agentId) {
    throw new NotFoundError();
  }

  if (error) {
    throw new StudioError('Cannot find agent. Please try again.');
  }

  const { appId } = depot.getState();

  const navigate = useNavigate();

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      const token: string = yield* unwrap(getAccessTokenSilently());
      try {
        const roomId = yield* unwrap(fetchRoomIdForAgentId(agentId, token));

        if (!roomId) {
          throw new NotFoundError();
        }

        // Join the LiveKit Room. Keep the one-shot token for the first mint;
        // getAuthToken re-mints after the SDK closes the engine.
        const lk = yield* unwrap(
          broker.create(roomId, {
            appId,
            signal,
            localEncoderIds: [toIdFromHex(agentId)!],
            token,
            getAuthToken: () => getAccessTokenSilently(),
            mode: 'cloud'
          })
        );

        const startedAt = Date.now();

        while (true) {
          if (signal.aborted) {
            return;
          }

          const agentIDUint8Array = toIdFromHex(agentId);

          if (!agentIDUint8Array) {
            throw new NotFoundError();
          }

          const participants = lk.listCurrentParticipants(agentIDUint8Array);

          if (participants.includes(agentId)) {
            navigate(Paths.room.boot.resolve(roomId), { replace: true });
            return;
          }

          if (Date.now() - startedAt >= MAX_WAIT_FOR_PARTICIPANT_MS) {
            navigate(Paths.room.boot.resolve(roomId), { replace: true });
            return;
          }

          yield new Promise((resolve) => setTimeout(resolve, WAIT_POLL_INTERVAL_MS));
        }
      } catch {
        if (signal.aborted) {
          return;
        }

        setError(true);
      }
    },
    [agentId, appId, getAccessTokenSilently, navigate]
  );

  return <Loader />;
};
